import {
  createContext,
  Fragment,
  type PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

import type { BreadcrumbSegment } from '@revelio/replots';

import styles from './ChartHeaderBreadcrumb.module.css';

/**
 * Drilldown breadcrumb a chart body publishes into its card header (v1.1
 * nested treemap): the chart title becomes the root crumb, followed by one
 * crumb per drilled level and the drilled view's total.
 */
export type ChartHeaderBreadcrumb = {
  /**
   * Drilled segments only (targetDepth 1..n); the title is the root crumb. A
   * segment may carry hover text (`title`) for a name the header ellipsizes;
   * crumbs without one show nothing on hover.
   */
  path: (BreadcrumbSegment & { title?: string })[];
  /** Formatted total of the drilled view, shown as "(10,881)". */
  totalLabel: string | null;
  onNavigate: (targetDepth: number) => void;
};

type Setter = (breadcrumb: ChartHeaderBreadcrumb | null) => void;

const BreadcrumbSetterContext = createContext<Setter | null>(null);
const BreadcrumbValueContext = createContext<ChartHeaderBreadcrumb | null>(
  null
);

export const ChartHeaderBreadcrumbProvider = ({
  children,
}: PropsWithChildren) => {
  const [breadcrumb, setBreadcrumb] = useState<ChartHeaderBreadcrumb | null>(
    null
  );
  return (
    <BreadcrumbSetterContext.Provider value={setBreadcrumb}>
      <BreadcrumbValueContext.Provider value={breadcrumb}>
        {children}
      </BreadcrumbValueContext.Provider>
    </BreadcrumbSetterContext.Provider>
  );
};

/**
 * Blocks a subtree from publishing into the surrounding card header — used
 * around the expand-modal chart copy, which has no header of its own and
 * would otherwise drive the page card's breadcrumb from inside the modal.
 * Charts under this boundary fall back to their in-plot breadcrumb.
 */
export const ChartHeaderBreadcrumbBoundary = ({
  children,
}: PropsWithChildren) => {
  return (
    <BreadcrumbSetterContext.Provider value={null}>
      <BreadcrumbValueContext.Provider value={null}>
        {children}
      </BreadcrumbValueContext.Provider>
    </BreadcrumbSetterContext.Provider>
  );
};

/** Setter for chart bodies; `null` when no header is listening. */
export const useChartHeaderBreadcrumbSetter = () =>
  useContext(BreadcrumbSetterContext);

export const useChartHeaderBreadcrumb = () =>
  useContext(BreadcrumbValueContext);

export const useDrilldownHeaderBreadcrumb = ({
  stack,
  totalLabel,
  onNavigate,
}: {
  stack: readonly { name: string }[];
  totalLabel: string | null;
  onNavigate: (targetDepth: number) => void;
}): boolean => {
  const setHeaderBreadcrumb = useChartHeaderBreadcrumbSetter();

  useEffect(() => {
    if (setHeaderBreadcrumb === null) return;
    if (stack.length === 0) {
      setHeaderBreadcrumb(null);
      return;
    }
    setHeaderBreadcrumb({
      path: stack.map((entry, index) => ({
        name: entry.name,
        targetDepth: index + 1,
      })),
      totalLabel,
      onNavigate,
    });
  }, [setHeaderBreadcrumb, stack, totalLabel, onNavigate]);

  useEffect(() => {
    if (setHeaderBreadcrumb === null) return;
    return () => setHeaderBreadcrumb(null);
  }, [setHeaderBreadcrumb]);

  return setHeaderBreadcrumb !== null;
};

type ChartHeaderBreadcrumbTrailProps = {
  rootTitle: string;
  breadcrumb: ChartHeaderBreadcrumb;
};

// v1.1 breadcrumb chevron (18×18, square caps, 70% strength).
const BreadcrumbChevron = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={styles.separator}
    aria-hidden="true"
  >
    <g opacity="0.7">
      <path
        d="M7.5 12L10.5 9L7.5 6"
        stroke="currentColor"
        strokeLinecap="square"
      />
    </g>
  </svg>
);

export const ChartHeaderBreadcrumbTrail = ({
  rootTitle,
  breadcrumb,
}: ChartHeaderBreadcrumbTrailProps) => {
  const { path, totalLabel, onNavigate } = breadcrumb;
  return (
    <nav
      className={styles.trail}
      aria-label="Chart drilldown breadcrumb"
      data-testid="chart-header-breadcrumb"
    >
      <button
        type="button"
        className={styles.link}
        data-testid="chart-header-breadcrumb-0"
        onClick={() => onNavigate(0)}
      >
        {rootTitle}
      </button>
      {path.map((segment, index) => {
        const isCurrent = index === path.length - 1;
        return (
          <Fragment key={segment.targetDepth}>
            <BreadcrumbChevron />
            {isCurrent ? (
              <span className={styles.current} title={segment.title}>
                {segment.name}
              </span>
            ) : (
              <button
                type="button"
                className={styles.link}
                title={segment.title}
                data-testid={`chart-header-breadcrumb-${segment.targetDepth}`}
                onClick={() => onNavigate(segment.targetDepth)}
              >
                {segment.name}
              </button>
            )}
          </Fragment>
        );
      })}
      {totalLabel !== null && (
        <span className={styles.total}>({totalLabel})</span>
      )}
    </nav>
  );
};
